import { Component, OnDestroy, QueryList, ViewChild, ViewChildren, ViewEncapsulation } from '@angular/core';
import { AnalyticNodeMode, Endpoint, HelpIndex } from '../../../models/enums';
import { isArray, isIP, validateIP } from '../../../models/utils';
import { BaseModalComponent } from '../../../models/base-modal-component';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { RestService } from '../../../services/rest/rest.service';
import { DrRequestInfo } from '../../../models/dr-request-info';
import { DrObject, DrObjectType, SupportedRegExType } from '../../../models/dr-object';
import { PopoverDirective } from 'ngx-bootstrap/popover';
import { MountedConnectorMode } from '../../../models/connector';
import { CommonService } from '../../../services/common/common.service';
import { ScrollablePopoverDirective } from '../../../directives/scrollable-popover/scrollable-popover.directive';

@Component({
  standalone: false,
  selector: 'res-new-realm-node-edit',
  templateUrl: './new-realm-node-edit.component.html',
  styleUrls: ['./new-realm-node-edit.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class NewRealmNodeEditComponent extends BaseModalComponent implements OnDestroy {
  @ViewChild('validationPop') validationPop: PopoverDirective;
  @ViewChildren(ScrollablePopoverDirective) errorPopovers: QueryList<ScrollablePopoverDirective>;

  modalTitle: string = 'New Realm Node';
  modalAutomationId = 'new-realm-node-modal';
  isBusy = false;
  isBusyMessage = '';
  modalClass = 'new-realm-node-modal';
  helpIndex = HelpIndex.sys_add_realm_node_dlg;

  nodeName: string;
  ipAddress: string;
  analyticNodeMode: AnalyticNodeMode = AnalyticNodeMode.STANDARD;
  storageName: string;
  storagePathList: any[] = [];
  selectedStoragePath: string = "";

  alreadyValidatedNameSuccess: boolean = false;
  alreadyValidatedStorageNameSuccess: boolean = false;
  alertIpAddress = { errorMessage: 'Not a valid IP address.', invalidIP: false };
  isStoragePathValid: boolean;
  isStoragePathValidatedSuccessfully: boolean = false;

  drObject = new DrObject(DrObjectType.REALM_NODE, SupportedRegExType.CONNECTOR);
  popoverErrors: {
    type: string,
    popover: boolean,
    message: string,
    isError: boolean,
    storageType: string,
    storagePopover: boolean,
    storageMessage: string,
    storageIsError: boolean
  } = {
      type: 'error',
      popover: false,
      message: "",
      isError: false,
      storageType: 'error',
      storagePopover: false,
      storageMessage: "",
      storageIsError: false,
    };

  constructor(private modalService: BsModalService, private modalRef: BsModalRef, private restService: RestService, private commonService: CommonService) {
    super(modalService, modalRef);
  }

  validateOnBlur(name: string, type: string): Promise<boolean> {
    if (type === 'Realm Node') {
      this.nodeName = name?.trim();
      this.alreadyValidatedNameSuccess = false;
    } else {
      this.alreadyValidatedStorageNameSuccess = false;
    }
    if (name) {
      return new Promise<boolean>((resolve) => {
        this.isBusy = true;
        this.isBusyMessage = type === 'Realm Node' ? 'Validating Realm Node Name ...' : 'Validating Storage Name ...';
        this.restService.post(Endpoint.viewManager_validateName, {
          name: name,
          objectType: type === 'Realm Node' ? this.drObject.objectType : DrObjectType.STORAGE,
          systemScope: true
        }).then(response => {
          this.isBusy = false;
          this.isBusyMessage = '';
          if (response.reason) {
            if (type === 'Realm Node') {
              this.popoverErrors.type = 'error';
              this.popoverErrors.message = response.reason;
              this.popoverErrors.popover = this.errorPopovers?.first?.closingByClick ? false : true;
              this.popoverErrors.isError = this.errorPopovers?.first?.closingByClick ? false : true;
              this.storageName = "";
            } else {
              this.popoverErrors.storageType = 'error';
              this.popoverErrors.storagePopover = true;
              this.popoverErrors.storageIsError = true;
            }
            resolve(false);
          } else {
            if (type === 'Realm Node') {
              this.popoverErrors.popover = false;
              this.popoverErrors.isError = false;
              this.alreadyValidatedNameSuccess = true;
            } else {
              this.popoverErrors.storagePopover = false;
              this.popoverErrors.storageIsError = false;
              this.alreadyValidatedStorageNameSuccess = true;
            }
            resolve(true);
          }
        }, error => {
          if (type === 'Realm Node') {
            this.popoverErrors.popover = false;
            this.popoverErrors.isError = false;
          } else {
            this.popoverErrors.storagePopover = false;
            this.popoverErrors.storageIsError = false;
          }
          this.isBusy = false;
          resolve(false);
        });
      });
    } else {
      return Promise.resolve(false);
    }
  }

  validateName(name: string, type: string): void {
    const errorMessage = this.drObject.uiValidateName(name);
    if (errorMessage?.length > 0) {
      if (type === 'Realm Node') {
        this.popoverErrors.message = errorMessage;
        this.popoverErrors.type = 'error';
        this.popoverErrors.popover = true;
        this.popoverErrors.isError = true;
      } else if (type === 'Storage') {
        this.popoverErrors.storageMessage = errorMessage;
        this.popoverErrors.storageType = 'error';
        this.popoverErrors.storagePopover = true;
        this.popoverErrors.storageIsError = true;
      }
    } else {
      if (type === 'Realm Node') {
        this.popoverErrors.popover = false;
        this.popoverErrors.isError = false;
      } else if (type === 'Storage') {
        this.popoverErrors.storagePopover = false;
        this.popoverErrors.storageIsError = false;
      }
    }
  }

  ok(): void {
    const endpoint = Endpoint.realmManager_createNode;
    const request = {
      name: this.nodeName,
      ipAddress: this.ipAddress,
      analyticNodeMode: this.analyticNodeMode,
      tempStorageName: this.storageName,
      tempStorageExport: this.selectedStoragePath,
      contextHandle: 'super_system_customer'
    }
    this.restService.post(endpoint, request).then(response => {
      this.isBusy = false;
      this.closeModal(true, response.node);
    }, error => {
      this.isBusy = false;
    });
  }

  checkOk(): void {
    if (this.alreadyValidatedNameSuccess && this.alreadyValidatedStorageNameSuccess) {
      this.ok();
    }
    if (!this.alreadyValidatedNameSuccess) {
      this.validateOnBlur(this.nodeName, 'Realm Node').then(validResponse => {
        if (validResponse) {
          this.ok();
        }
      });
    }
    if (!this.alreadyValidatedStorageNameSuccess) {
      this.validateOnBlur(this.storageName, 'Storage').then(validResponse => {
        if (validResponse) {
          this.ok();
        }
      });
    }
  }

  okEnabled(): boolean {
    let enabled = false;
    if (!DrObject.isEmpty(this.nodeName) &&
      (!this.popoverErrors.isError && !this.popoverErrors.popover) &&
      (!this.popoverErrors.storageIsError && !this.popoverErrors.storagePopover) &&
      !DrObject.isEmpty(this.ipAddress) &&
      isIP(this.ipAddress) === true &&
      !DrObject.isEmpty(this.storageName) &&
      !DrObject.isEmpty(this.selectedStoragePath) &&
      this.isStoragePathValidatedSuccessfully) {
      enabled = true;
    }
    return enabled;
  }

  validateStoragePathEnabled(): boolean {
    return this.selectedStoragePath?.length > 0 && this.ipAddress?.length > 0 && (isIP(this.ipAddress) === true);
  }

  onClickStandardType(): void {
    this.analyticNodeMode = AnalyticNodeMode.STANDARD;
  }

  onClickExpressType(): void {
    this.analyticNodeMode = AnalyticNodeMode.EXPRESS;
  }

  onClickOcrType(): void {
    this.analyticNodeMode = AnalyticNodeMode.OCR;
  }

  ngModelChangeIpLoadingStoragePathList = (ipAddress: string): void => {
    this.selectedStoragePath = "";
    if (validateIP(ipAddress)) {
      this.storagePathList = [];
      this.isBusy = true;
      this.isBusyMessage = 'Loading Storage Paths ...';
      this.loadStoragePathList();
    } else {
      this.storagePathList = [];
    }
  }

  onChangeIpAddress(ipAddress: string): void {
    this.alertIpAddress.invalidIP = !validateIP(ipAddress);
  }

  onChangeNodeName(name: string): void {
    this.storageName =
      (name.length > 0 && !this.popoverErrors.isError && !this.popoverErrors.popover)
        ? `${name}_temp`
        : "";
    this.popoverErrors.storagePopover = false;
    this.popoverErrors.storageIsError = false;
  }

  setStoragePath = (storagePath: string): void => {
    this.selectedStoragePath = storagePath;
    this.isStoragePathValidatedSuccessfully = false;
  }

  loadStoragePathList = (): void => {
    const requestInfo = new DrRequestInfo({
      endpoint: Endpoint.orgManager_getNfsMounts,
      responseName: 'mounts',
      data: {
        remoteHost: this.ipAddress,
        timeoutSeconds: 1
      }
    });
    this.restService.postWithObservable(requestInfo.endpoint, requestInfo.data).subscribe((response) => {
      if (response) {
        if (isArray(response[requestInfo.responseName])) {
          this.storagePathList = response[requestInfo.responseName] as Array<any>;
          this.isBusy = false;
          this.isBusyMessage = '';
        }
      }
    }, (error) => {
      if (error) {
        this.isBusy = false;
        this.isBusyMessage = '';
      }
    });
  }

  validateStoragePath(): void {
    this.isStoragePathValidatedSuccessfully = false;
    this.isBusy = true;
    this.isBusyMessage = 'Validating Storage Path ... ';
    this.restService.post(Endpoint.connectorManager_validateNFSConnector, {
      mountedConnectorMode: MountedConnectorMode.CLASSIC,
      remoteHost: this.ipAddress.localeCompare('localhost') === 0 ? this.commonService.debugServerIp : this.ipAddress,
      remotePath: this.selectedStoragePath,
      readOnly: true,
      systemScope: true,
    }).then((validateReponse) => {
      this.isStoragePathValid = validateReponse ? validateReponse.valid : false;
      this.validationPop.hide();
      this.validationPop.containerClass = this.isStoragePathValid ? 'success' : 'error';
      this.validationPop.show();
      this.isBusy = false;
      this.isStoragePathValidatedSuccessfully = true;
    }, () => {
      this.isBusy = false;
      this.isStoragePathValid = false;
      this.validationPop.hide();
      this.validationPop.containerClass = 'error';
      this.validationPop.show();
    })
  }

  resetClickClosed(): void {
    if (this.errorPopovers?.first?.closingByClick) {
      this.errorPopovers.first.closingByClick = false
    }
  }

  ngOnDestroy(): void {
    if (this.errorPopovers?.first?.closingByClick) {
      this.errorPopovers.first.closingByClick = false
    }
  }
}